import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useLayoutEffect, useRef, useState } from "react";
import Flex from "./Flex";
import Text from "./Text";
import { TimelineIcon } from "./svg-icons/navbarIcons";
import { DATE_FORMAT } from "../utils/date-time-utility";

type PresetKey =
  | "all_time"
  | "today"
  | "yesterday"
  | "this_week"
  | "this_month"
  | "last_month"
  | "this_year"
  | "last_year";

interface PresetOption {
  key: PresetKey;
  label: string;
  getRange: () => { start: Dayjs | null; end: Dayjs | null };
}

const PRESET_OPTIONS: PresetOption[] = [
  {
    key: "all_time",
    label: "All time",
    getRange: () => ({ start: null, end: null }),
  },
  {
    key: "today",
    label: "Today",
    getRange: () => ({ start: dayjs(), end: dayjs() }),
  },
  {
    key: "yesterday",
    label: "Yesterday",
    getRange: () => ({
      start: dayjs().subtract(1, "day"),
      end: dayjs().subtract(1, "day"),
    }),
  },
  {
    key: "this_week",
    label: "This week",
    getRange: () => ({
      start: dayjs().startOf("week"),
      end: dayjs().endOf("week"),
    }),
  },
  {
    key: "this_month",
    label: "This month",
    getRange: () => ({
      start: dayjs().startOf("month"),
      end: dayjs().endOf("month"),
    }),
  },
  {
    key: "last_month",
    label: "Last month",
    getRange: () => ({
      start: dayjs().subtract(1, "month").startOf("month"),
      end: dayjs().subtract(1, "month").endOf("month"),
    }),
  },
  {
    key: "this_year",
    label: "This year",
    getRange: () => ({
      start: dayjs().startOf("year"),
      end: dayjs().endOf("year"),
    }),
  },
  {
    key: "last_year",
    label: "Last year",
    getRange: () => ({
      start: dayjs().subtract(1, "year").startOf("year"),
      end: dayjs().subtract(1, "year").endOf("year"),
    }),
  },
];

const DAYS_OF_WEEK = ["Mo", "Tu", "We", "Th", "Fr", "Sat", "Su"];

interface SelectDateRangePickerProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onApply: (start: Dayjs | null, end: Dayjs | null) => void;
  buttonSx?: Record<string, any>;
  popoverSx?: Record<string, any>;
  /** Auto-apply this preset's range on first mount (and surface it as
   *  the active preset row). Only fires when both startDate and endDate
   *  are null — the picker won't override a range the parent already
   *  has, so the prop is safe to leave on across remounts. */
  defaultPreset?: PresetKey;
  /** Preset rows to drop from the left panel. Nothing is hidden unless the
   *  caller asks for it, so existing usages keep the full list. When every
   *  preset is hidden the panel itself is dropped and only the calendar
   *  remains. */
  hiddenPresets?: PresetKey[];
  /** Label shown on the trigger button while no range is selected. */
  placeholder?: string;
}

const SelectDateRangePicker = ({
  startDate,
  endDate,
  onApply,
  buttonSx,
  popoverSx,
  defaultPreset,
  hiddenPresets,
  placeholder = "Select Dates",
}: SelectDateRangePickerProps) => {
  const visiblePresets = PRESET_OPTIONS.filter(
    (preset) => !hiddenPresets?.includes(preset.key)
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(startDate ?? dayjs());
  const [tempStart, setTempStart] = useState<Dayjs | null>(startDate);
  const [tempEnd, setTempEnd] = useState<Dayjs | null>(endDate);
  const [activePreset, setActivePreset] = useState<PresetKey | null>(
    defaultPreset && !startDate && !endDate ? defaultPreset : null
  );

  // Auto-apply defaultPreset once, only when the parent hasn't already
  // got a range. We use a ref guard so the effect doesn't loop on the
  // onApply state update, and so a later parent reset to null doesn't
  // re-fire the preset (it should only seed the very first render).
  const defaultAppliedRef = useRef(false);
  useLayoutEffect(() => {
    if (defaultAppliedRef.current) return;
    if (!defaultPreset) return;
    if (startDate || endDate) {
      defaultAppliedRef.current = true;
      return;
    }
    const preset = PRESET_OPTIONS.find((p) => p.key === defaultPreset);
    if (!preset) return;
    const { start, end } = preset.getRange();
    defaultAppliedRef.current = true;
    onApply(start, end);
  }, [defaultPreset, startDate, endDate, onApply]);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTempStart(startDate);
    setTempEnd(endDate);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePresetClick = (preset: PresetOption) => {
    const { start, end } = preset.getRange();
    setTempStart(start);
    setTempEnd(end);
    setActivePreset(preset.key);
    if (start) setCurrentMonth(start);
  };

  const handleDayClick = (day: Dayjs) => {
    setActivePreset(null);
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(day);
      setTempEnd(null);
    } else {
      if (day.isBefore(tempStart, "day")) {
        setTempEnd(tempStart);
        setTempStart(day);
      } else {
        setTempEnd(day);
      }
    }
  };

  const handleApply = () => {
    onApply(tempStart, tempEnd ?? tempStart);
    handleClose();
  };

  const handleClearAll = () => {
    setTempStart(null);
    setTempEnd(null);
    setActivePreset(null);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, "month"));
  };

  // Build calendar grid
  const firstDayOfMonth = currentMonth.startOf("month");
  // Monday=0 based offset
  const startOffset = (firstDayOfMonth.day() + 6) % 7;
  const daysInMonth = currentMonth.daysInMonth();
  const calendarStart = firstDayOfMonth.subtract(startOffset, "day");

  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const days: Dayjs[] = [];
  for (let i = 0; i < totalCells; i++) {
    days.push(calendarStart.add(i, "day"));
  }

  const isInRange = (day: Dayjs) => {
    if (!tempStart || !tempEnd) return false;
    return day.isAfter(tempStart, "day") && day.isBefore(tempEnd, "day");
  };

  const isStart = (day: Dayjs) => tempStart?.isSame(day, "day") ?? false;
  const isEnd = (day: Dayjs) => tempEnd?.isSame(day, "day") ?? false;
  const isCurrentMonth = (day: Dayjs) => day.month() === currentMonth.month();
  const isToday = (day: Dayjs) => day.isSame(dayjs(), "day");

  const getButtonLabel = () => {
    if (startDate && endDate) {
      if (startDate.isSame(endDate, "day")) {
        return startDate.format(DATE_FORMAT);
      }
      return `${startDate.format(DATE_FORMAT)} - ${endDate.format(DATE_FORMAT)}`;
    }
    return placeholder;
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={<TimelineIcon />}
        sx={{
          textTransform: "none",
          color: "#344054",
          borderColor: "#D0D5DD",
          fontWeight: 500,
          fontSize: 14,
          whiteSpace: "nowrap",
          px: 2,
          "&:hover": { borderColor: "#98A2B3", backgroundColor: "#F9FAFB" },
          ...buttonSx,
        }}
      >
        {getButtonLabel()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "8px",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
              ...popoverSx,
            },
          },
        }}
      >
        <Flex>
          {/* Left Panel - Presets */}
          {visiblePresets.length > 0 && (
            <Box
              sx={{
                width: 160,
                borderRight: "1px solid #E0E0E0",
                py: 1,
              }}
            >
              <List disablePadding dense>
                {visiblePresets.map((preset) => (
                  <ListItemButton
                    key={preset.key}
                    selected={activePreset === preset.key}
                    onClick={() => handlePresetClick(preset)}
                    sx={{
                      py: 0.75,
                      px: 2,
                      "&.Mui-selected": {
                        backgroundColor: "#EBF5FF",
                        color: "#1B4F72",
                        fontWeight: 600,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#DCEEFB",
                      },
                    }}
                  >
                    <ListItemText
                      primary={preset.label}
                      primaryTypographyProps={{ fontSize: 14 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          {/* Right Panel - Calendar */}
          <Box
            sx={{ width: 300, p: 1.5 }}
            display={"flex"}
            justifyContent={"space-between"}
            flexDirection={"column"}
          >
            {/* Month Navigation */}
            <Flex alignItems="center" justifyContent="space-between" mb={1.5}>
              <IconButton size="small" onClick={handlePrevMonth}>
                <ChevronLeftIcon />
              </IconButton>
              <Text fontWeight={600} fontSize={14}>
                {currentMonth.format("MMMM YYYY")}
              </Text>
              <IconButton size="small" onClick={handleNextMonth}>
                <ChevronRightIcon />
              </IconButton>
            </Flex>

            {/* Day Headers */}
            <Box
              display="grid"
              gridTemplateColumns="repeat(7, 1fr)"
              textAlign="center"
              mb={0.5}
            >
              {DAYS_OF_WEEK.map((d) => (
                <Text key={d} fontSize={12} color="#667085" fontWeight={500}>
                  {d}
                </Text>
              ))}
            </Box>

            {/* Calendar Days */}
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
              {days.map((day, idx) => {
                const inRange = isInRange(day);
                const start = isStart(day);
                const end = isEnd(day);
                const curMonth = isCurrentMonth(day);
                const today = isToday(day);
                const selected = start || end;

                return (
                  <Box
                    key={idx}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 36,
                      // Range background
                      ...(inRange && {
                        backgroundColor: "#DCEEFB",
                      }),
                      ...(start &&
                        tempEnd && {
                          background:
                            "linear-gradient(to right, transparent 50%, #DCEEFB 50%)",
                        }),
                      ...(end &&
                        tempStart && {
                          background:
                            "linear-gradient(to left, transparent 50%, #DCEEFB 50%)",
                        }),
                      ...(start && end && { background: "transparent" }),
                    }}
                  >
                    <Box
                      onClick={() => curMonth && handleDayClick(day)}
                      sx={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        cursor: curMonth ? "pointer" : "default",
                        fontSize: 13,
                        fontWeight: selected ? 600 : 400,
                        color: !curMonth
                          ? "#D0D5DD"
                          : selected
                            ? "#fff"
                            : "#344054",
                        backgroundColor: selected ? "#1B4F72" : "transparent",
                        border:
                          today && !selected ? "1px solid #98A2B3" : "none",
                        "&:hover": curMonth
                          ? {
                              backgroundColor: selected ? "#163E5C" : "#F2F4F7",
                            }
                          : {},
                      }}
                    >
                      {day.date()}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Actions */}
            <Flex justifyContent="flex-end" gap={1.5} mt={2}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearAll}
                sx={{
                  textTransform: "none",
                  color: "#344054",
                  borderColor: "#D0D5DD",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#1B4F72",
                  fontSize: 13,
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "#163E5C" },
                }}
              >
                Apply
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Popover>
    </>
  );
};

export default SelectDateRangePicker;
